import React from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useChannelMembers } from '@components/Channels/useChannelMembers';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { ModalContext } from '@context/modal';
import { type ChannelMembersQuery, UserModalRowFragmentDoc } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { getSingularOrPlural } from '@utils/helpers';
import { EmptyUserStat } from '../UserStats/EmptyState';
import {
  CHANNEL_MEMBERS_EMPTY_HEADER,
  CHANNEL_MEMBERS_EMPTY_SUBTEXT,
  MAX_UNVIRTUALIZED_ITEMS,
  StyledScrollArea,
} from '../UserStats/shared';
import { UserRow, UserRowSkeleton } from '../shared/UserList/UserRow';

const userItemContent = (
  i: number,
  node: ChannelMembersQuery['channelMembers']['edges'][number]['node'],
) => {
  const { id } = getFragment(UserModalRowFragmentDoc, node.user);
  return (
    <div key={`user-${id}-${i}`} className="px-6">
      <UserRow user={node.user} sourceLocation="Channel Page" />
    </div>
  );
};

export const ChannelMembersModal = ({
  channelId,
  membersNumber,
}: {
  channelId: string;
  membersNumber: number;
}) => {
  const { channelMembers, isLoading, loadMore } = useChannelMembers({
    channelId,
    limit: MAX_UNVIRTUALIZED_ITEMS,
  });

  const { closeCancelModal } = ModalContext.useContainer();

  const isEmpty = channelMembers.length === 0 || !channelMembers;
  const [scrollParent, setScrollParent] = React.useState<HTMLDivElement | undefined>();
  const [isAtTop, setIsAtTop] = React.useState(true);

  const initialUsers = React.useMemo(
    () => channelMembers.slice(0, MAX_UNVIRTUALIZED_ITEMS),
    [channelMembers],
  );

  const virtualizedUsers = React.useMemo(
    () => channelMembers.slice(MAX_UNVIRTUALIZED_ITEMS),
    [channelMembers],
  );

  const LoadingFooter = React.useCallback(
    () =>
      isLoading ? (
        <div className="h-6 px-6">
          <UserRowSkeleton />
          <UserRowSkeleton />
          <UserRowSkeleton />
        </div>
      ) : (
        <div className="h-6" />
      ),
    [isLoading],
  );

  const body = (
    <StyledScrollArea
      ref={r => {
        setScrollParent(r || undefined);
      }}
      empty={isEmpty}
      onScroll={e => {
        if (!isAtTop && e.currentTarget.scrollTop <= 0) {
          setIsAtTop(true);
        }

        if (e.currentTarget.scrollTop > 0 && isAtTop) {
          setIsAtTop(false);
        }
      }}
    >
      {isEmpty && !isLoading && (
        <EmptyUserStat
          header={CHANNEL_MEMBERS_EMPTY_HEADER}
          subText={CHANNEL_MEMBERS_EMPTY_SUBTEXT}
        />
      )}

      {!isEmpty && (
        <>
          {/* Virtuoso takes a while to render so only shows 1 user on intial mount this it to always show prefetched data and not have to wait for virtuoso  */}
          {initialUsers.length > 0 && initialUsers.map((user, i) => userItemContent(i, user))}
          {virtualizedUsers.length > 0 && (
            <Virtuoso
              data={virtualizedUsers}
              itemContent={userItemContent}
              customScrollParent={scrollParent}
              endReached={loadMore}
              components={{ Footer: LoadingFooter }}
            />
          )}
        </>
      )}
    </StyledScrollArea>
  );

  return (
    <StandardModal
      title={`${membersNumber} ${getSingularOrPlural({ word: 'member', count: membersNumber })}`}
      closeModal={closeCancelModal}
      bodyClassName="px-0 mx-0"
      body={body}
    />
  );
};
